import { Controller } from "@hotwired/stimulus"

// Drives a navbar dropdown (trigger + flyout panel) with a "safe triangle" hover
// bridge. Hovering the trigger opens the panel, but the panel sits a few pixels
// below the trigger — closing on the first mouseleave makes that gap (and the
// diagonal travel toward the first item) almost impossible to cross: the menu
// collapses mid-aim. Instead, when the pointer leaves we keep the menu open as
// long as it stays inside the triangle formed by its exit point and the panel's
// two near corners — i.e. while it is plausibly *aiming* at the panel. This is
// the technique Amazon's mega-menu popularized and Floating UI ships as
// `safePolygon`.
//
// Visibility is JS-driven via a `data-open` attribute (CSS reveals the panel on
// `group-data-[open]` and on `group-focus-within`, so keyboard/AT users get it
// for free). Click toggles, Escape closes, aria-expanded stays synced — the menu
// is never hover-only.
export default class extends Controller {
  static targets = ["trigger", "panel"]

  // How long the pointer may stall inside the safe triangle before we give up
  // and close. Active travel toward the panel refreshes this, so it only bites
  // when the pointer stops in the dead zone and never arrives.
  static values = { closeDelay: { type: Number, default: 250 } }

  connect() {
    this.onFocusOut = this.onFocusOut.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerEnter = this.onPointerEnter.bind(this)
    this.onPointerLeave = this.onPointerLeave.bind(this)

    this.onReflow = this.scheduleAnchor.bind(this)
    // Synchronous, not coalesced: focus-within reveals the panel in the same
    // frame, and until it is anchored it is still clipped by the strip.
    this.onFocusIn = this.anchor.bind(this)

    this.element.addEventListener("focusout", this.onFocusOut)
    this.element.addEventListener("pointerenter", this.onPointerEnter)
    this.element.addEventListener("pointerleave", this.onPointerLeave)
    this.element.addEventListener("focusin", this.onFocusIn)
    window.addEventListener("resize", this.onReflow)
    window.addEventListener("scroll", this.onReflow, true)
  }

  disconnect() {
    this.element.removeEventListener("focusout", this.onFocusOut)
    this.element.removeEventListener("pointerenter", this.onPointerEnter)
    this.element.removeEventListener("pointerleave", this.onPointerLeave)
    this.element.removeEventListener("focusin", this.onFocusIn)
    window.removeEventListener("resize", this.onReflow)
    window.removeEventListener("scroll", this.onReflow, true)
    if (this.anchorFrame) window.cancelAnimationFrame(this.anchorFrame)
    this.stopTracking()
  }

  // Only a visible panel is worth measuring. Without this guard every scroll
  // anywhere in the document — an overview's own scrolling table body, say —
  // would run anchor()'s read-then-write against each nav menu on the page and
  // force two synchronous layouts per frame for a panel nobody is looking at.
  // The rAF also coalesces a burst of scroll events into one write.
  scheduleAnchor() {
    if (!this.visible) return
    if (this.anchorFrame) return

    this.anchorFrame = window.requestAnimationFrame(() => {
      this.anchorFrame = null
      if (this.visible) this.anchor()
    })
  }

  // The strip of nav destinations scrolls horizontally once they no longer fit,
  // and a scroll container clips its overflow on both axes — the panel's default
  // `absolute top-full` would be sliced off at the strip's bottom edge. So an
  // open panel is lifted out of the flow (position: fixed) and pinned to its
  // trigger here, clamped so the right-most menu never opens off-screen. The
  // default stays `absolute` in the markup on purpose: if the bundle fails or
  // has not connected yet, the CSS-only focus-within reveal still lands the
  // flyout under its button instead of in the viewport's top-left corner.
  //
  // Scrolling the strip while the menu is open can carry the trigger out of
  // view (the pointer never leaves the element, so no pointerleave fires). A
  // flyout with no visible owner is worse than none, so that closes the menu.
  anchor() {
    if (!this.hasPanelTarget || !this.hasTriggerTarget) return

    const trigger = this.triggerTarget.getBoundingClientRect()
    if (this.triggerHidden(trigger)) {
      this.close()
      return
    }

    const margin = 8
    const width = this.panelTarget.offsetWidth
    const left = Math.min(Math.max(margin, trigger.left), window.innerWidth - width - margin)

    this.panelTarget.style.position = "fixed"
    this.panelTarget.style.left = `${Math.round(Math.max(margin, left))}px`
    this.panelTarget.style.top = `${Math.round(trigger.bottom + 4)}px`
  }

  // Scrolled out of the strip that owns it, or out of the viewport entirely.
  triggerHidden(rect) {
    if (rect.right <= 0 || rect.left >= window.innerWidth) return true

    const strip = this.element.closest("[data-test-id='nav-strip']")
    if (!strip) return false

    const box = strip.getBoundingClientRect()
    return rect.right <= box.left || rect.left >= box.right
  }

  toggle() {
    this.expanded ? this.close() : this.open()
  }

  open() {
    this.stopTracking()
    this.element.setAttribute("data-open", "true")
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.anchor()
  }

  // Closing must leave no focus behind inside the group: the reveal CSS also
  // matches on :focus-within (for keyboard/AT users when the bundle hasn't
  // connected yet), so a trigger that stays focused after data-open is removed
  // keeps the panel visually open — the DOM would disagree with itself, and a
  // caller reading checkVisibility() would see it as still open indefinitely.
  close() {
    this.stopTracking()
    this.element.removeAttribute("data-open")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    if (this.element.contains(document.activeElement)) this.triggerTarget.blur()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  // Closing once focus leaves the whole menu keeps aria-expanded honest when a
  // keyboard user tabs out of the flyout.
  onFocusOut(event) {
    if (!this.element.contains(event.relatedTarget)) this.close()
  }

  // Hovering opens — but only a real mouse hovers. A touch tap also fires
  // pointerenter (pointerType "touch"); honouring it would open-then-toggle-shut
  // in one gesture, so touch is left to the click toggle. Gating per event
  // (not on a device-wide `(hover: hover)` media query) also means headless
  // Chrome, which reports `hover: none`, still drives this from synthetic mouse
  // input in tests.
  onPointerEnter(event) {
    if (event.pointerType === "touch") return
    this.open()
  }

  // The pointer left the trigger/panel subtree. Don't close yet — watch where it
  // goes and only close once it leaves the safe triangle or stalls there.
  onPointerLeave(event) {
    if (event.pointerType === "touch") return
    if (!this.expanded) return

    this.safeTriangle = this.buildSafeTriangle(event.clientX, event.clientY)
    if (!this.safeTriangle) {
      this.close()
      return
    }

    document.addEventListener("pointermove", this.onPointerMove)
    this.armStallTimer()
  }

  onPointerMove(event) {
    if (this.element.contains(event.target)) {
      // Back over the trigger or panel — pointerenter keeps it open; stop bridging.
      this.stopTracking()
      return
    }

    if (this.pointInTriangle(event.clientX, event.clientY, this.safeTriangle)) {
      this.armStallTimer()
      return
    }

    this.close()
  }

  armStallTimer() {
    window.clearTimeout(this.stallTimer)
    this.stallTimer = window.setTimeout(() => this.close(), this.closeDelayValue)
  }

  stopTracking() {
    document.removeEventListener("pointermove", this.onPointerMove)
    window.clearTimeout(this.stallTimer)
    this.safeTriangle = null
  }

  // Triangle from the pointer's exit point (apex) to the panel's near edge
  // (base), so the cone is narrow at the cursor and widens to the whole panel —
  // exactly the area the pointer would sweep while aiming at an item. The base is
  // widened sideways and nudged a little *into* the panel so grazing the boundary
  // is forgiven. Our layout always drops the panel below the trigger, but we
  // derive the near edge from geometry so it stays correct if that ever flips.
  buildSafeTriangle(x, y) {
    const rect = this.panelTarget.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return null

    const pad = 12
    const panelBelow = y <= rect.top
    const edgeY = panelBelow ? rect.top + pad : rect.bottom - pad
    return [
      [x, y],
      [rect.left - pad, edgeY],
      [rect.right + pad, edgeY],
    ]
  }

  pointInTriangle(px, py, [[ax, ay], [bx, by], [cx, cy]]) {
    const sign = (x1, y1, x2, y2, x3, y3) => (x1 - x3) * (y2 - y3) - (x2 - x3) * (y1 - y3)
    const d1 = sign(px, py, ax, ay, bx, by)
    const d2 = sign(px, py, bx, by, cx, cy)
    const d3 = sign(px, py, cx, cy, ax, ay)
    const hasNeg = d1 < 0 || d2 < 0 || d3 < 0
    const hasPos = d1 > 0 || d2 > 0 || d3 > 0
    return !(hasNeg && hasPos)
  }

  get expanded() {
    return this.triggerTarget.getAttribute("aria-expanded") === "true"
  }

  // Both reveal routes: the pointer/click one sets data-open, and a keyboard
  // user gets the panel from the CSS-only focus-within rule alone.
  get visible() {
    return this.expanded || this.element.contains(document.activeElement)
  }
}
